'use client';

import '~/lib/i18n-club';

import dynamic from 'next/dynamic';
import { useParams } from 'next/navigation';
import { Provider } from 'react-redux';
import store from 'redux/store';
import { ThemeProvider } from 'styled-components';
import { SWRConfig } from 'swr';
import theme from 'theme';
import RestoreAuth from '~/components/auth/RestoreAuth';
import ClubContext from '~/components/ClubContext';
import FloatingPortal from '~/components/FloatingPortal';
import { swrRetryPolicy } from '~/lib/apiErrors';
import { swrFetcher } from '~/lib/swr-fetcher';

// Neither draws anything until something is opened or reported — the login dialog,
// a "gesendet" after the contact form — so both arrive after the page is usable
// instead of in front of it.
const ModalManager = dynamic(() => import('~/components/ModalManager'), { ssr: false });
const ToastManager = dynamic(() => import('~/components/ToastManager/ToastManager'), { ssr: false });

/**
 * What a club website runs inside, and nothing more.
 *
 * The app's shell brought every translation of every module in four languages (about
 * 670 KB over the wire on its own), the consent banner, the e-mail verification strip
 * and the global overlay to a page that uses none of them; with the rest it was 3 MB of
 * JavaScript and an 8-second LCP on a mid-range phone. A club site needs the session
 * (the header shows who is signed in), data fetching for its widgets, a place for the
 * login dialog and for toasts. The club layout brings its own landmarks, so there is
 * no <main> here either.
 */
export default function ClubShell({ children }: { children: React.ReactNode }) {
    const params = useParams();

    return (
        <ThemeProvider theme={theme}>
            <SWRConfig value={{ fetcher: swrFetcher, ...swrRetryPolicy }}>
                <Provider store={store}>
                    <ClubContext.Provider
                        value={Array.isArray(params?.slug) ? params.slug[0] : (params?.slug as string)}
                    >
                        <div>{children}</div>
                        <ToastManager />
                        <ModalManager />
                        <FloatingPortal />
                        <RestoreAuth />
                    </ClubContext.Provider>
                </Provider>
            </SWRConfig>
        </ThemeProvider>
    );
}
